import { yupResolver } from '@hookform/resolvers/yup'
import { EFinancingProgram, EProvince } from '@if/core/types'
import { formatCurrency, normalizeNumber } from '@if/core/utils'
import { ControlledCheckbox } from '@if/mui'
import { Info as InfoIcon } from '@mui/icons-material'
import { Alert, AlertTitle, Box, debounce, Grid, IconButton, Paper, Stack, Tooltip, Typography } from '@mui/material'
import AsyncActionButton from '@src/components/AsyncActionButton'
import ConflictDialog from '@src/containers/App/components/ConflictDialog'
import {
  useGetIfXpressFunding,
  usePostIfXpressWorksheet,
  usePutIfXpressWorksheet,
} from '@src/data/api/worksheet-api/ifxpress-worksheet-api'
import { useGetPossibleTermsIfXpress } from '@src/data/api/worksheet-api/worksheet-api'
import {
  createNewIfXpressWorksheetDto,
  getIfXpressFundingDto,
  isVariableInterest,
  updateIfXpressWorksheet,
} from '@src/data/worksheet-selectors'
import { Conflict } from '@src/types/Conflict'
import { Constants, FinancingProgramConfigs } from '@src/types/Constants'
import { CreditApplication } from '@src/types/CreditApplicationSchema'
import { Merchant } from '@src/types/Merchant'
import { EditIfXpressWorksheetDto, buildEditIfXpressWorksheetDtoSchema } from '@src/types/WorksheetSchema'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useLoanTermSelection } from '../../ViewCreditApplicationPage/worksheet-hooks'
import IfXpressFundingSummary from './components/IfXpressFundingSummary'
import WorksheetDetails from './components/IfXpressWorksheetDetails'

interface Props {
  merchant: Merchant
  creditApplication: CreditApplication
  merchantPayments: { label: string; value: string; id: string }[]
}
const IfXpressWorksheet = ({ merchant, creditApplication, merchantPayments }: Props) => {
  const EditIfXpressWorksheetDtoSchema = buildEditIfXpressWorksheetDtoSchema(
    creditApplication.applicant.hardHitReportReceivedOn!,
    creditApplication.finalCreditDecision.maxAmountFinanced!,
    creditApplication.merchantPaymentPlanId,
  )

  const defaultValues = creditApplication.worksheet
    ? creditApplication.worksheet
    : EditIfXpressWorksheetDtoSchema.getDefault()

  const formMethods = useForm<EditIfXpressWorksheetDto>({
    mode: 'onBlur',
    defaultValues: defaultValues as unknown as EditIfXpressWorksheetDto,
    resolver: yupResolver(EditIfXpressWorksheetDtoSchema),
  })

  const { setValue, watch, handleSubmit, control } = formMethods
  const [postWorksheet, isPosting, isPostError, postError, resetPost] = usePostIfXpressWorksheet()
  const [putWorksheet, isPuting, isPutError, updateError, resetPut] = usePutIfXpressWorksheet()
  const [openInsuranceInfoBox, setOpenInsuranceInfoBox] = useState(false)
  const deliveryOn = watch('deliveryOn')
  const amountRequested = normalizeNumber(watch('amountRequested')?.toString()) ?? 0
  const [debouncedAmountRequested, setDebouncedAmountRequested] = useState(amountRequested)
  const debouncedSetAmountRequested = useMemo(
    () => debounce((value: number) => setDebouncedAmountRequested(value), Constants.AmountRequestedDebounceMs),
    [],
  )
  useEffect(() => {
    debouncedSetAmountRequested(amountRequested)
    return () => {
      debouncedSetAmountRequested.clear()
    }
  }, [amountRequested, debouncedSetAmountRequested])
  const paymentFrequency = watch('paymentFrequency') ?? 'monthly'
  const term = watch('term')
  const firstPaymentOn = watch('firstPaymentOn')!
  const paymentPlanId = watch('paymentPlanId')
  const includeInsurance = watch('includeInsurance')
  const hasVariableInterest = isVariableInterest(paymentPlanId)
  const [error, setError] = useState<Error | null>(null)

  const navigate = useNavigate()
  const { t } = useTranslation()
  const conflict = postError
    ? (postError?.response?.data as unknown as Conflict)
    : (updateError?.response?.data as unknown as Conflict)

  const dto = useMemo(() => {
    return getIfXpressFundingDto(
      creditApplication.id!,
      debouncedAmountRequested,
      paymentFrequency,
      term,
      deliveryOn,
      firstPaymentOn,
      includeInsurance,
      creditApplication.applicant.currentAddress.stateIso,
      paymentPlanId,
      creditApplication.finalCreditDecision.interestRate,
      creditApplication.coApplicant !== null,
    )
  }, [
    creditApplication,
    debouncedAmountRequested,
    deliveryOn,
    firstPaymentOn,
    includeInsurance,
    paymentFrequency,
    paymentPlanId,
    term,
  ])
  const computeMonthTermFrequency = useMemo(
    () => ({
      merchantId: creditApplication.merchantId,
      paymentPlanId,
      interestRate: creditApplication.finalCreditDecision.interestRate,
      hasCoapplicant: creditApplication.coApplicant !== null,
      stateIso: creditApplication.applicant.currentAddress.stateIso!,
      paymentFrequency,
      deliveryOn,
      firstPaymentOn,
      maxPmtAmount: creditApplication.finalCreditDecision.maxPmtAmount,
      amountRequested: debouncedAmountRequested,
      includeInsurance,
      include84Months: (creditApplication.finalCreditDecision.maxAmountFinanced ?? 0) >= Constants.LargeLoanAmount,
    }),
    [
      creditApplication.merchantId,
      paymentPlanId,
      creditApplication.finalCreditDecision.interestRate,
      creditApplication.coApplicant,
      creditApplication.applicant.currentAddress.stateIso,
      paymentFrequency,
      deliveryOn,
      firstPaymentOn,
      creditApplication.finalCreditDecision.maxPmtAmount,
      creditApplication.finalCreditDecision.maxAmountFinanced,
      debouncedAmountRequested,
      includeInsurance,
    ],
  )

  const [possibleTerms, isLoadingTerms] = useGetPossibleTermsIfXpress(computeMonthTermFrequency)
  const { loanTermChoicesList, handlePaymentPlanChange } = useLoanTermSelection({
    possibleTerms,
    computeMonthTermFrequency,
    creditApplication,
    merchant,
    paymentPlanId,
    term,
    regularMinTerm: FinancingProgramConfigs[EFinancingProgram.IfXpress].minTerm,
    setValue,
  })

  const [computedIfXpressFunding, isComputing] = useGetIfXpressFunding(creditApplication.id!, dto)
  const doesTermSupportInsurance = useMemo(() => {
    if (!possibleTerms?.monthlyTerms) return false
    const matchingTerm = possibleTerms.monthlyTerms.find((item) => item.term === Number(term))

    return matchingTerm ? matchingTerm.effectiveRateWithInsuranceIsLegal : false
  }, [possibleTerms, term])

  useEffect(() => {
    if (
      term === Constants.LongTerm &&
      (creditApplication?.finalCreditDecision.maxAmountFinanced ?? 0) < Constants.LargeLoanAmount &&
      creditApplication?.finalCreditDecision.maxTermDuration
    )
      setValue('term', creditApplication?.finalCreditDecision.maxTermDuration)
  }, [
    creditApplication?.finalCreditDecision.maxAmountFinanced,
    term,
    creditApplication?.finalCreditDecision.maxTermDuration,
    setValue,
  ])

  const navigateToView = (financingProgramId: string, id: string) => {
    navigate(`/credits/${financingProgramId}/${id}`)
  }

  const saveWorksheet = async (formData: EditIfXpressWorksheetDto) => {
    if (creditApplication.worksheet == null) {
      await postWorksheet({ worksheet: createNewIfXpressWorksheetDto(creditApplication.id!, formData) }).then(() => {
        navigateToView(creditApplication.financingProgramId, creditApplication.id!)
      })
    } else {
      await putWorksheet({
        worksheet: updateIfXpressWorksheet(formData, creditApplication),
      }).then(() => {
        navigateToView(creditApplication.financingProgramId, creditApplication.id!)
      })
    }
  }
  const onSubmit = async (formData: EditIfXpressWorksheetDto) => {
    await saveWorksheet(formData)
  }

  const isAPRTooHigh = useMemo(() => {
    const address = creditApplication.applicant.currentAddress
    if (!address || !computedIfXpressFunding) return false

    const { effectiveRate } = computedIfXpressFunding
    const { stateIso } = address

    const aprThresholds: Record<string, number> = {
      [EProvince.Newfoundland]: 27,
    }

    const threshold = aprThresholds[stateIso!]

    return threshold !== undefined && effectiveRate >= threshold
  }, [computedIfXpressFunding, creditApplication.applicant.currentAddress])

  const hasConflict = updateError?.response?.status === 409 || postError?.response?.status === 409
  const handleClose = useCallback(() => {
    if (updateError?.response?.status === 409) {
      resetPut()
    }
    if (postError?.response?.status === 409) {
      resetPost()
    }
  }, [resetPost, resetPut, updateError, postError])

  useEffect(() => {
    if (computedIfXpressFunding?.paymentForFrequency > computeMonthTermFrequency?.maxPmtAmount) {
      setError(new Error(t('common.errors.maxpaymentExceedsMaximumAllowed')))
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }
    setError(null)
  }, [computedIfXpressFunding?.paymentForFrequency, computeMonthTermFrequency?.maxPmtAmount, t])

  return (
    <FormProvider {...formMethods}>
      <div>
        {(postError || updateError) && !hasConflict && (
          <Paper>
            <Alert severity="error">
              <Typography>{postError?.response?.data?.message || updateError?.response?.data?.message}</Typography>
            </Alert>
          </Paper>
        )}
        {error && (
          <Alert
            severity="error"
            sx={{
              width: '100%',
              marginBottom: '2rem',
            }}
          >
            <AlertTitle>{t('common.error')}</AlertTitle>
            {error.message}
          </Alert>
        )}
        {hasConflict && <ConflictDialog updatedOn={conflict?.updatedOn} open={hasConflict} onClose={handleClose} />}
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack direction={{ md: 'column', xs: 'column-reverse' }} spacing={5} justifyContent="space-between">
            <Stack
              direction={{ sm: 'row', xs: 'column' }}
              spacing={2}
              justifyContent="space-between"
              alignItems={{ sm: 'self-end', xs: 'self-start' }}
            >
              <Stack direction="column" justifyContent="space-between">
                <Typography variant="h4">{t('worksheet.worksheet')}</Typography>
                <Typography variant="h5">
                  {t('worksheet.maxLoanAmount')}:{' '}
                  {formatCurrency(creditApplication.finalCreditDecision.maxAmountFinanced)}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={2} display="flex">
                <AsyncActionButton fullWidth={false} variant="contained" onClick={() => navigate(-1)}>
                  {t('common.previous')}
                </AsyncActionButton>
                <AsyncActionButton
                  fullWidth={false}
                  variant="contained"
                  disabled={isAPRTooHigh || isComputing || !computedIfXpressFunding || error != null}
                  isPending={isPosting || isPuting}
                  isError={isPostError || isPutError}
                  onClick={handleSubmit(onSubmit)}
                >
                  {t('worksheet.createContract')}
                </AsyncActionButton>
              </Stack>
            </Stack>

            <Stack direction={{ md: 'row', xs: 'column-reverse' }} spacing={5} justifyContent="space-between">
              <Box sx={{ flex: 1 }}>
                <Grid container spacing={2}>
                  <Grid size={12}>
                    <WorksheetDetails
                      creditApplication={creditApplication}
                      merchantPayments={merchantPayments}
                      loanTermChoicesList={loanTermChoicesList}
                      isLoadingTerm={isLoadingTerms}
                      planPromoRules={merchant.planPromoRules}
                      paymentFrequency={paymentFrequency}
                      onPaymentPlanChange={handlePaymentPlanChange}
                    />
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      sm: 6,
                      md: 4,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <ControlledCheckbox
                        name="includeInsurance"
                        control={control}
                        label={t('worksheet.addProtectionPlan')}
                        disabled={!doesTermSupportInsurance}
                      />
                      <Tooltip title={t('worksheet.includeProtectionPlan')}>
                        <IconButton onClick={() => setOpenInsuranceInfoBox((prev) => !prev)}>
                          <InfoIcon style={{ fontSize: 'medium' }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Grid>

                  {openInsuranceInfoBox && (
                    <Box component="div" sx={{ padding: '10px', marginLeft: '15px' }}>
                      <Typography variant="h5" sx={{ marginBottom: '10px' }}>
                        {t('worksheet.insuranceIncitative')}
                      </Typography>
                      <ul style={{ paddingLeft: '20px' }}>
                        <li>
                          <Typography variant="body1" sx={{ marginBottom: '10px' }}>
                            {t('worksheet.lifeInsurance')}
                          </Typography>
                        </li>
                        <li>
                          <Typography variant="body1" sx={{ marginBottom: '10px' }}>
                            {t('worksheet.accidentalDisability')}
                          </Typography>
                        </li>
                      </ul>
                    </Box>
                  )}
                  <Grid container sx={{ display: { md: 'none' } }} size={12}>
                    <Grid
                      size={{
                        xs: 12,
                        sm: 6,
                      }}
                    >
                      <AsyncActionButton color="error" onClick={() => navigate(-1)}>
                        {t('common.previous')}
                      </AsyncActionButton>
                    </Grid>
                    <Grid
                      size={{
                        xs: 12,
                        sm: 6,
                      }}
                    >
                      <AsyncActionButton
                        variant="contained"
                        color="primary"
                        disabled={isAPRTooHigh || isComputing || !computedIfXpressFunding}
                        isPending={isPosting || isPuting}
                        isError={isPostError || isPutError}
                        onClick={handleSubmit(onSubmit)}
                      >
                        {t('worksheet.createContract')}
                      </AsyncActionButton>
                    </Grid>
                  </Grid>
                </Grid>
              </Box>
              <Box
                sx={{
                  border: 1,
                  padding: 2,
                  maxWidth: { xs: '100%', md: '350px' },
                }}
              >
                <IfXpressFundingSummary
                  creditApplication={creditApplication}
                  amountRequested={amountRequested}
                  hasVariableInterest={hasVariableInterest}
                  firstPaymentOn={firstPaymentOn}
                  merchant={merchant}
                  paymentPlanId={paymentPlanId}
                  computedIfXpressFunding={computedIfXpressFunding}
                  isComputing={isComputing}
                />
              </Box>
            </Stack>
          </Stack>
        </form>
      </div>
    </FormProvider>
  )
}

export default IfXpressWorksheet
